// src/services/webhookService.js
import axios from 'axios';
import { API_CONFIG } from '../config/api';

const API_BASE_URL = API_CONFIG.BASE_URL;

const api = () => {
  const token = localStorage.getItem('token') || localStorage.getItem('authToken');
  if (!token) throw new Error('No authentication token found. Please login again.');
  return axios.create({
    baseURL: API_BASE_URL,
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
  });
};

const handle = (error, op) => {
  const detail = error.response?.data?.detail;
  if (error.response?.status === 401) throw new Error('Authentication failed.');
  if (detail) {
    if (Array.isArray(detail)) throw new Error(detail.map(e => e.msg || JSON.stringify(e)).join('; '));
    throw new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
  }
  throw new Error(error.message || `${op} failed`);
};

export const webhookService = {
  listWebhooks: async () => {
    try {
      const r = await api().get('/api/webhooks');
      return r.data;
    } catch (e) { handle(e, 'List webhooks'); }
  },

  createWebhook: async (data) => {
    try {
      const r = await api().post('/api/webhooks', data);
      if (r.data?.success === false) throw new Error(r.data.error || 'Create failed');
      return r.data;
    } catch (e) { handle(e, 'Create webhook'); }
  },

  deleteWebhook: async (webhookId) => {
    try {
      const r = await api().delete(`/api/webhooks/${webhookId}`);
      return r.data;
    } catch (e) { handle(e, 'Delete webhook'); }
  },

  linkWorkflow: async (webhookId, workflowId) => {
    try {
      const r = await api().patch(`/api/webhooks/${webhookId}/link`, { workflow_id: workflowId });
      return r.data;
    } catch (e) { handle(e, 'Link workflow'); }
  },

  setAppSecret: async (webhookId, appSecret) => {
    try {
      const r = await api().patch(`/api/webhooks/${webhookId}/app-secret`, { app_secret: appSecret });
      return r.data;
    } catch (e) { handle(e, 'Set App Secret'); }
  },

  // Short form: /wh/<22-char base64url of the UUID>. Some providers (Gupshup)
  // reject the 106-char /api/webhooks/receive/<uuid> URL; the short one is 74.
  // The backend decodes it back to the same UUID; the long URL still works.
  getReceiveUrl: (webhookId) => {
    const shortId = toShortWebhookId(webhookId);
    return shortId
      ? `${API_BASE_URL}/wh/${shortId}`
      : `${API_BASE_URL}/api/webhooks/receive/${webhookId}`;
  },
};

// UUID string -> unpadded base64url of its 16 bytes. Returns '' for anything
// that is not a UUID so the caller can fall back to the long URL.
export const toShortWebhookId = (webhookId) => {
  const hex = String(webhookId || '').replace(/-/g, '');
  if (!/^[0-9a-fA-F]{32}$/.test(hex)) return '';
  const bytes = hex.match(/.{2}/g).map(h => parseInt(h, 16));
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
};

export default webhookService;
